
// Parameter Display Component
import React, { useState } from 'react';
import { Button, Icon, Input } from 'semantic-ui-react';

const ParameterDisplay: React.FC<{
  parameters: string;
  onEditParameter: (key: string, newValue: any) => void;
  onRemoveParameter: (key: string) => void;
  allowRemove?: boolean;
}> = ({ parameters, onEditParameter, onRemoveParameter, allowRemove = true }) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const parseParameters = () => {
    // Handle empty or null parameters
    if (!parameters || parameters.trim() === '') {
      return {};
    }

    // Clean up the parameters string
    let cleanedParameters = parameters.trim();

    // Handle common malformed JSON cases
    if (cleanedParameters === 'null' || cleanedParameters === 'undefined') {
      return {};
    }

    try {
      const parsed = JSON.parse(cleanedParameters);
      return parsed;
    } catch (error) {
      console.error('ParameterDisplay - JSON parse error:', error);
      console.error('ParameterDisplay - Problematic string:', JSON.stringify(cleanedParameters));
      console.error('ParameterDisplay - First 10 characters:', cleanedParameters.substring(0, 10));

      // Try to fix common JSON issues
      try {
        // Remove any leading/trailing whitespace and fix common issues
        let fixedJson = cleanedParameters;

        // If it starts with a quote but doesn't look like a proper JSON object, wrap it
        if (fixedJson.startsWith('"') && !fixedJson.startsWith('{"')) {
          fixedJson = `{"value": ${fixedJson}}`;
        }

        const parsed = JSON.parse(fixedJson);
        return parsed;
      } catch (fixError) {
        console.error('ParameterDisplay - Could not fix JSON, returning empty object:', fixError);
        return {};
      }
    }
  };

  const getParameterType = (value: any): string => {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'string') {
      // Check if it's a date
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'date';
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return 'datetime';
      return 'text';
    }
    return 'text';
  };

  const formatValue = (value: any, type: string): string => {
    switch (type) {
      case 'boolean':
        return value ? 'True' : 'False';
      case 'number':
        return value.toString();
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'datetime':
        return new Date(value).toLocaleString();
      default:
        return String(value);
    }
  };

  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'boolean':
        return 'toggle on';
      case 'number':
        return 'hashtag';
      case 'date':
        return 'calendar';
      case 'datetime':
        return 'clock';
      default:
        return 'text cursor';
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'boolean':
        return 'purple';
      case 'number':
        return 'blue';
      case 'date':
        return 'green';
      case 'datetime':
        return 'orange';
      default:
        return 'grey';
    }
  };

  const handleEdit = (key: string, currentValue: any) => {
    setEditingKey(key);
    setEditValue(String(currentValue));
  };

  const handleSaveEdit = () => {
    if (editingKey) {
      const parsedValue = parseFloat(editValue);
      const finalValue = isNaN(parsedValue) ? editValue : parsedValue;
      onEditParameter(editingKey, finalValue);
      setEditingKey(null);
      setEditValue('');
    }
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const params = parseParameters();
  const paramKeys = Object.keys(params);

  if (paramKeys.length === 0) {
    // Check if we have invalid JSON that couldn't be parsed
    if (parameters && parameters.trim() !== '' && parameters.trim() !== '{}') {
      return (
        <div
          style={{
            padding: '16px',
            backgroundColor: '#fff3cd',
            borderRadius: '4px',
            textAlign: 'center',
            color: '#856404',
            border: '1px solid #ffeaa7',
          }}
        >
          <strong>Invalid JSON parameters detected.</strong>
          <br />
          Raw data:{' '}
          <code style={{ fontSize: '0.8em' }}>
            {parameters.substring(0, 50)}
            {parameters.length > 50 ? '...' : ''}
          </code>
          <br />
          <small>Edit the JSON directly to fix the parameters.</small>
        </div>
      );
    }

    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          textAlign: 'center',
          color: '#666',
          border: '1px dashed #ddd',
        }}
      >
        No parameters set.
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
      {paramKeys.map(key => {
        const value = params[key];
        const type = getParameterType(value);
        const isEditing = editingKey === key;

        return (
          <div
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              borderBottom: '1px solid #eee',
              backgroundColor: isEditing ? '#f0f8ff' : 'transparent',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Icon
                name={getTypeIcon(type) as any}
                color={getTypeColor(type) as any}
                style={{ marginRight: '8px' }}
              />
              <strong style={{ marginRight: '8px', minWidth: '120px' }}>{key}:</strong>

              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Input
                    value={editValue}
                    onChange={(_, { value }) => setEditValue(value)}
                    style={{ flex: 1, marginRight: '8px' }}
                    autoFocus
                  />
                  <Button size="mini" icon="check" color="green" onClick={handleSaveEdit} />
                  <Button size="mini" icon="close" color="red" onClick={handleCancelEdit} />
                </div>
              ) : (
                <span style={{ flex: 1, marginRight: '8px' }}>{formatValue(value, type)}</span>
              )}
            </div>

            {!isEditing && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <Button
                  size="mini"
                  icon="edit"
                  onClick={() => handleEdit(key, value)}
                  title="Edit parameter"
                />
                {allowRemove && (
                  <Button
                    size="mini"
                    icon="trash"
                    color="red"
                    onClick={() => onRemoveParameter(key)}
                    title="Remove parameter"
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ParameterDisplay;
